import 'reflect-metadata';
import { injectable, inject } from 'inversify';
import ThirdPartyTypes from '../../di/ThirdPartyTypes';

import { decodeJwt } from 'jose';
import ILocalStorageContainer from './ILocalStorageContainer';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { EventTypes } from '../../utils/applicationInsights';
import UserIdStorage from './UserIdStorage';
import type IUserIdStorage from './IUserIdStorage';

@injectable()
export default class LocalStorageContainer implements ILocalStorageContainer {
  @inject(ThirdPartyTypes.LocalStorage)
  private localStorage!: Storage;

  @inject(ThirdPartyTypes.ApplicationInsights)
  private appInsights!: ApplicationInsights;

  @inject(UserIdStorage)
  private userIdStorage!: IUserIdStorage;

  getTokenByKey(key: string): string | null {
    return this.localStorage.getItem(key);
  }

  setTokenByKey(key: string, token: string): void {
    this.localStorage.setItem(key, token);
  }

  getClientId() {
    const urlParams = new URLSearchParams(window.location.search);
    const clientId = urlParams.get('client_id');
    return clientId;
  }

  getUserId() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('user_id') || this.userIdStorage?.getUserId();
  }

  isTokenExpired(token: string) {
    try {
      const { exp } = decodeJwt(token);
      return !exp || exp * 1000 < Date.now();
    } catch (e) {
      this.appInsights.trackEvent?.({
        name: EventTypes.TOKEN_ERROR,
        properties: {
          error: JSON.stringify(e),
          message: 'error occured at decoding token'
        }
      });
      return true;
    }
  }

  getItemByClientId(key: string): string | null {
    return this.localStorage.getItem(`${this.getClientId()}_${key}`);
  }

  getTokenByClientId(key: string): string | null {
    const token = this.localStorage.getItem(`${this.getClientId()}_${key}`);
    if (!token) {
      this.appInsights.trackEvent?.({
        name: EventTypes.TOKEN_ERROR,
        properties: {
          tokenKey: key,
          message: 'token is null'
        }
      });
      return null;
    }
    const isExpired = this.isTokenExpired(token);
    if (isExpired) {
      this.appInsights.trackEvent?.({
        name: EventTypes.TOKEN_ERROR,
        properties: {
          tokenKey: key,
          message: 'token has expired'
        }
      });
      return null;
    }
    return token;
  }

  setTokenByClientId(key: string, token: string): void {
    this.localStorage.setItem(`${this.getClientId()}_${key}`, token);
  }

  getItemByUserId(key: string) {
    const item = this.localStorage.getItem(`${this.getUserId()}_${key}`);
    if (!item) return null;
    return JSON.parse(item) as any;
  }

  setItemByUserId(key: string, item: any): void {
    this.localStorage.setItem(`${this.getUserId()}_${key}`, JSON.stringify(item));
  }
}
